import { useState } from "react";
import { Box, Button, HStack, Spinner, Text, VStack } from "@chakra-ui/react";
import { HiPaperClip, HiChevronDown, HiChevronUp } from "react-icons/hi2";
import {
    Field, FormInput, FormTextarea, FormSwitch, FormSelect,
    CollapsibleSection, ItemCard, ItemHeader, AddButton, RemoveButton, RepeatableText,
    Grid2, Grid3, isFieldInvalid,
} from "@/components/jobOffer/ui";
import { toaster } from "@/components/ui/toaster";
import { extractBusinessErrorMessage } from "@/services/apiErrorMessage";
import { CampaignService } from "@/services/CampaignService";
import { createEmptyProduct, PRODUCT_TYPE_OPTIONS, BILLING_CYCLE_OPTIONS } from "@/models/campaign";

const attachmentUrl = (path) =>
    /^https?:\/\//i.test(path)
        ? path // legacy attachment (pre-task-31 "List<string> URL" model) -- Path holds the original external link, not one of our storage object keys
        : `${process.env.REACT_APP_BASE_URL || "/mlapi"}/campaign/attachments/file?name=${encodeURIComponent(path)}`;

const ProductAttachments = ({ attachments, onChange, uploadInputId }) => {
    const [isUploading, setIsUploading] = useState(false);

    const handleFilesSelected = async (e) => {
        const files = Array.from(e.target.files ?? []);
        e.target.value = "";
        if (!files.length) return;
        setIsUploading(true);
        try {
            const uploaded = await CampaignService.uploadProductAttachments(files);
            onChange([...(attachments ?? []), ...(uploaded ?? [])]);
        } catch (error) {
            toaster.create({ type: "error", title: extractBusinessErrorMessage(error) ?? "Nie udało się wgrać załącznika" });
        } finally {
            setIsUploading(false);
        }
    };

    const remove = (i) => onChange((attachments ?? []).filter((_, idx) => idx !== i));

    return (
        <Box>
            <HStack justify="space-between" mb={1.5}>
                <Text fontSize="xs" fontWeight={600} color="text.grey" textTransform="uppercase" letterSpacing="wider">Załączniki</Text>
                <Button as="label" htmlFor={uploadInputId} variant="outlined" size="xs" disabled={isUploading} cursor="pointer">
                    {isUploading ? <Spinner size="xs" /> : <HiPaperClip />}
                    Dodaj plik
                    <input id={uploadInputId} type="file" multiple style={{ display: "none" }} onChange={handleFilesSelected} />
                </Button>
            </HStack>
            <VStack align="stretch" gap={2}>
                {(attachments ?? []).map((att, i) => (
                    <HStack key={i} gap={2}>
                        <Text fontSize="sm" color="text.white" flex={1} truncate>{att.name}</Text>
                        {att.path && (
                            <Box as="a" href={attachmentUrl(att.path)} target="_blank" rel="noreferrer" fontSize="xs" color="brand.400">Otwórz</Box>
                        )}
                        <RemoveButton onClick={() => remove(i)} />
                    </HStack>
                ))}
                {(attachments ?? []).length === 0 && <Text fontSize="sm" color="text.grey">Brak załączników</Text>}
            </VStack>
        </Box>
    );
};

const ParamsEditor = ({ parameters, onChange }) => {
    const entries = Object.entries(parameters ?? {});
    const update = (i, key, val) => {
        const next = [...entries];
        next[i] = [key, val];
        onChange(Object.fromEntries(next));
    };
    const remove = (i) => onChange(Object.fromEntries(entries.filter((_, idx) => idx !== i)));
    const add = () => onChange({ ...parameters, "": "" });

    return (
        <Box>
            <Text fontSize="xs" fontWeight={600} color="text.grey" textTransform="uppercase" letterSpacing="wider" mb={1.5}>Parametry</Text>
            <VStack align="stretch" gap={2}>
                {entries.map(([key, val], i) => (
                    <HStack key={i} gap={2}>
                        <FormInput value={key} onChange={(e) => update(i, e.target.value, val)} placeholder="Nazwa parametru" flex={1} />
                        <FormInput value={val} onChange={(e) => update(i, key, e.target.value)} placeholder="Wartość" flex={1} />
                        <RemoveButton onClick={() => remove(i)} />
                    </HStack>
                ))}
                <AddButton label="Dodaj parametr" onClick={add} />
            </VStack>
        </Box>
    );
};

const FaqEditor = ({ faq, onChange }) => {
    const update = (i, patch) => onChange(faq.map((f, idx) => idx === i ? { ...f, ...patch } : f));
    const remove = (i) => onChange(faq.filter((_, idx) => idx !== i));
    const add = () => onChange([...faq, { question: "", answer: "" }]);

    return (
        <Box>
            <Text fontSize="xs" fontWeight={600} color="text.grey" textTransform="uppercase" letterSpacing="wider" mb={1.5}>FAQ</Text>
            <VStack align="stretch" gap={2}>
                {faq.map((f, i) => (
                    <Box key={i} borderWidth={1} borderColor="border.primary" borderRadius={8} p={3}>
                        <HStack gap={2} align="flex-start">
                            <VStack flex={1} gap={1.5}>
                                <Text fontSize="xs" fontWeight={600} color="text.grey">{`FAQ #${i + 1}`}</Text>
                                <Field label="Pytanie"><FormInput value={f.question} onChange={(e) => update(i, { question: e.target.value })} placeholder="Pytanie" /></Field>
                                <Field label="Odpowiedź"><FormTextarea value={f.answer} onChange={(e) => update(i, { answer: e.target.value })} placeholder="Odpowiedź" rows={2} /></Field>
                            </VStack>
                            <RemoveButton onClick={() => remove(i)} />
                        </HStack>
                    </Box>
                ))}
                <AddButton label="Dodaj pytanie FAQ" onClick={add} />
            </VStack>
        </Box>
    );
};

const Step3Products = ({ formik, values, setValues, forceOpen }) => {
    const products = values.products ?? [];
    const setProducts = (next) => setValues({ ...values, products: next });
    const updateProduct = (i, patch) => setProducts(products.map((p, idx) => idx === i ? { ...p, ...patch } : p));
    const updateNested = (i, section, patch) => updateProduct(i, { [section]: { ...products[i][section], ...patch } });
    const addProduct = () => setProducts([...products, createEmptyProduct()]);
    const removeProduct = (i) => setProducts(products.filter((_, idx) => idx !== i));

    return (
        <VStack w="full" align="stretch" gap={4}>
            <CollapsibleSection title="Lista produktów" subtitle="Dowolna liczba produktów lub usług — pola dopasowują się do wybranego typu" defaultOpen forceOpen={forceOpen}>
                <VStack align="stretch" gap={4}>
                    {products.map((p, i) => {
                        const isPhysical = p.productType === "PhysicalProduct";
                        const isService = p.productType === "Service";
                        const isSubscription = p.productType === "Subscription";
                        const isOther = p.productType === "Other" || !p.productType;
                        const showAll = isOther && p.showAllFields;
                        // "Show this section" -- true for the type(s) it belongs to, or for "Inny" once
                        // the user opts into "Pokaż więcej pól" (showAll covers every optional section at once).
                        const show = (...forTypes) => forTypes.some(Boolean) || showAll;

                        const hasExtraPricing = show(isPhysical, isService) || show(isService, isSubscription);
                        const hasAvailability = show(isPhysical) || show(isService, isSubscription);

                        return (
                            <ItemCard key={p.id ?? i}>
                                <ItemHeader title={p.name || "Nowy produkt"} index={i} onRemove={() => removeProduct(i)} />
                                <VStack align="stretch" gap={3}>
                                    <Grid2>
                                        <Field label="Nazwa" required invalid={isFieldInvalid(formik, `products.${i}.name`)}>
                                            <FormInput value={p.name} onChange={(e) => updateProduct(i, { name: e.target.value })} />
                                        </Field>
                                        <Field label="Typ" required invalid={isFieldInvalid(formik, `products.${i}.productType`)} hint="Steruje tym, jakie pola pojawiają się w sekcjach poniżej">
                                            <FormSelect value={p.productType ?? ""} onChange={(v) => updateProduct(i, { productType: v || null })} options={PRODUCT_TYPE_OPTIONS} placeholder="Wybierz typ" />
                                        </Field>
                                    </Grid2>
                                    <Field label="Krótki opis"><FormTextarea value={p.shortDescription ?? ""} onChange={(e) => updateProduct(i, { shortDescription: e.target.value })} rows={2} /></Field>
                                    <Grid2>
                                        <Field label="Cena"><FormInput type="number" value={p.price?.price ?? ""} onChange={(e) => updateNested(i, "price", { price: e.target.value === "" ? null : Number(e.target.value) })} /></Field>
                                        <Field label="Waluta"><FormInput value={p.price?.currency ?? ""} onChange={(e) => updateNested(i, "price", { currency: e.target.value })} /></Field>
                                    </Grid2>

                                    <CollapsibleSection title="Opis i zastosowanie" subtitle="Kategoria, pełny opis, zastosowania, korzyści — opcjonalne">
                                        <VStack align="stretch" gap={3}>
                                            <Field label="Kategoria"><FormInput value={p.category ?? ""} onChange={(e) => updateProduct(i, { category: e.target.value })} /></Field>
                                            <Field label="Pełny opis"><FormTextarea value={p.fullDescription ?? ""} onChange={(e) => updateProduct(i, { fullDescription: e.target.value })} rows={3} /></Field>
                                            <Grid3>
                                                <RepeatableText label="Zastosowanie" values={p.applications ?? []} onChange={(v) => updateProduct(i, { applications: v })} placeholder="Zastosowanie" />
                                                <RepeatableText label="Korzyści" values={p.benefits ?? []} onChange={(v) => updateProduct(i, { benefits: v })} placeholder="Korzyść" />
                                                <RepeatableText label="USP" values={p.usp ?? []} onChange={(v) => updateProduct(i, { usp: v })} placeholder="USP" />
                                            </Grid3>
                                            {show(isPhysical, isService) && (
                                                <Field label="Instrukcja użytkowania / sposób korzystania"><FormTextarea value={p.usageInstructions ?? ""} onChange={(e) => updateProduct(i, { usageInstructions: e.target.value })} rows={2} /></Field>
                                            )}
                                        </VStack>
                                    </CollapsibleSection>

                                    {hasExtraPricing && (
                                        <CollapsibleSection title="Cena — szczegóły" subtitle="Widełki, rabaty, opłaty dodatkowe, cykl rozliczeniowy — opcjonalne">
                                            <VStack align="stretch" gap={3}>
                                                {show(isPhysical, isService) && (
                                                    <>
                                                        <Field label="Sposób wyceny"><FormInput value={p.price?.pricingMethod ?? ""} onChange={(e) => updateNested(i, "price", { pricingMethod: e.target.value })} /></Field>
                                                        <Grid2>
                                                            <Field label="Cena od"><FormInput type="number" value={p.price?.priceFrom ?? ""} onChange={(e) => updateNested(i, "price", { priceFrom: e.target.value === "" ? null : Number(e.target.value) })} /></Field>
                                                            <Field label="Cena do"><FormInput type="number" value={p.price?.priceTo ?? ""} onChange={(e) => updateNested(i, "price", { priceTo: e.target.value === "" ? null : Number(e.target.value) })} /></Field>
                                                        </Grid2>
                                                        <Grid2>
                                                            <RepeatableText label="Rabaty / promocje" values={p.price?.discounts ?? []} onChange={(v) => updateNested(i, "price", { discounts: v })} placeholder="Rabat" />
                                                            <RepeatableText label="Dodatkowe koszty" values={p.price?.additionalCosts ?? []} onChange={(v) => updateNested(i, "price", { additionalCosts: v })} placeholder="Dodatkowy koszt" />
                                                        </Grid2>
                                                        <Field label="Warunki uzyskania promocji"><FormTextarea value={p.price?.promoConditions ?? ""} onChange={(e) => updateNested(i, "price", { promoConditions: e.target.value })} rows={2} /></Field>
                                                    </>
                                                )}
                                                {show(isService, isSubscription) && (
                                                    <Grid3>
                                                        <Field label="Opłata wdrożeniowa"><FormInput type="number" value={p.price?.implementationCost ?? ""} onChange={(e) => updateNested(i, "price", { implementationCost: e.target.value === "" ? null : Number(e.target.value) })} /></Field>
                                                        <Field label="Opłata jednorazowa"><FormInput type="number" value={p.price?.oneTimeFee ?? ""} onChange={(e) => updateNested(i, "price", { oneTimeFee: e.target.value === "" ? null : Number(e.target.value) })} /></Field>
                                                        {show(isSubscription) && (
                                                            <Field label="Cykl rozliczeniowy">
                                                                <FormSelect value={p.price?.billingCycle ?? ""} onChange={(v) => updateNested(i, "price", { billingCycle: v || null })} options={BILLING_CYCLE_OPTIONS} placeholder="Wybierz cykl" />
                                                            </Field>
                                                        )}
                                                    </Grid3>
                                                )}
                                            </VStack>
                                        </CollapsibleSection>
                                    )}

                                    {show(isPhysical) && (
                                        <CollapsibleSection title="Producent i parametry" subtitle="Opcjonalne">
                                            <VStack align="stretch" gap={3}>
                                                <Grid3>
                                                    <Field label="Producent"><FormInput value={p.manufacturer ?? ""} onChange={(e) => updateProduct(i, { manufacturer: e.target.value })} /></Field>
                                                    <Field label="Marka"><FormInput value={p.brand ?? ""} onChange={(e) => updateProduct(i, { brand: e.target.value })} /></Field>
                                                    <Field label="Model"><FormInput value={p.model ?? ""} onChange={(e) => updateProduct(i, { model: e.target.value })} /></Field>
                                                </Grid3>
                                                <Field label="Wariant"><FormInput value={p.variant ?? ""} onChange={(e) => updateProduct(i, { variant: e.target.value })} /></Field>
                                                <ParamsEditor parameters={p.parameters} onChange={(v) => updateProduct(i, { parameters: v })} />
                                            </VStack>
                                        </CollapsibleSection>
                                    )}

                                    {hasAvailability && (
                                        <CollapsibleSection title="Dostępność i termin" subtitle="Opcjonalne">
                                            <VStack align="stretch" gap={3}>
                                                {show(isPhysical) && (
                                                    <>
                                                        <Grid2>
                                                            <FormSwitch label="Dostępny" checked={p.availability?.isAvailable ?? true} onChange={(v) => updateNested(i, "availability", { isAvailable: v })} />
                                                            <FormSwitch label="Ograniczona liczba" checked={!!p.availability?.limitedQuantity} onChange={(v) => updateNested(i, "availability", { limitedQuantity: v })} />
                                                        </Grid2>
                                                        <Grid3>
                                                            <Field label="Termin realizacji"><FormInput value={p.availability?.leadTime ?? ""} onChange={(e) => updateNested(i, "availability", { leadTime: e.target.value })} /></Field>
                                                            <Field label="Czas dostawy"><FormInput value={p.availability?.deliveryTime ?? ""} onChange={(e) => updateNested(i, "availability", { deliveryTime: e.target.value })} /></Field>
                                                            <Field label="Dostępność lokalna"><FormInput value={p.availability?.localAvailability ?? ""} onChange={(e) => updateNested(i, "availability", { localAvailability: e.target.value })} /></Field>
                                                        </Grid3>
                                                    </>
                                                )}
                                                {show(isService, isSubscription) && (
                                                    <Grid2>
                                                        {show(isService) && (
                                                            <Field label="Termin realizacji"><FormInput value={p.availability?.leadTime ?? ""} onChange={(e) => updateNested(i, "availability", { leadTime: e.target.value })} /></Field>
                                                        )}
                                                        <Field label="Termin rozpoczęcia usługi"><FormInput type="date" value={p.availability?.serviceStartDate ?? ""} onChange={(e) => updateNested(i, "availability", { serviceStartDate: e.target.value || null })} /></Field>
                                                    </Grid2>
                                                )}
                                            </VStack>
                                        </CollapsibleSection>
                                    )}

                                    {show(isPhysical) && (
                                        <CollapsibleSection title="Bezpieczeństwo" subtitle="Opcjonalne">
                                            <VStack align="stretch" gap={3}>
                                                <Grid2>
                                                    <RepeatableText label="Przeciwwskazania" values={p.safetyInfo?.contraindications ?? []} onChange={(v) => updateNested(i, "safetyInfo", { contraindications: v })} placeholder="Przeciwwskazanie" />
                                                    <RepeatableText label="Ostrzeżenia" values={p.safetyInfo?.warnings ?? []} onChange={(v) => updateNested(i, "safetyInfo", { warnings: v })} placeholder="Ostrzeżenie" />
                                                </Grid2>
                                                <Field label="Ograniczenia wiekowe"><FormInput value={p.safetyInfo?.ageRestrictions ?? ""} onChange={(e) => updateNested(i, "safetyInfo", { ageRestrictions: e.target.value })} placeholder="np. 18+" /></Field>
                                            </VStack>
                                        </CollapsibleSection>
                                    )}

                                    {isOther && (
                                        <Box
                                            as="button" type="button"
                                            onClick={() => updateProduct(i, { showAllFields: !p.showAllFields })}
                                            w="full" py={2.5} px={4}
                                            borderWidth={1} borderStyle="dashed"
                                            borderColor={p.showAllFields ? "purple.300" : "gray.300"}
                                            borderRadius={8}
                                            bg={p.showAllFields ? "purple.50" : "transparent"}
                                            color={p.showAllFields ? "purple.600" : "gray.500"}
                                            fontSize="sm" fontWeight={600}
                                            display="flex" alignItems="center" justifyContent="center" gap={2}
                                            _hover={{ borderColor: "purple.400", color: "purple.600", bg: "purple.50" }}
                                            _active={{ transform: "scale(0.99)" }}
                                            transition="border-color 0.15s, color 0.15s, background 0.15s, transform 0.1s"
                                        >
                                            <Box as={p.showAllFields ? HiChevronUp : HiChevronDown} boxSize={4} />
                                            {p.showAllFields ? "Ukryj dodatkowe pola" : "Pokaż więcej pól (producent, dostępność, bezpieczeństwo, cykl rozliczeniowy...)"}
                                        </Box>
                                    )}

                                    <CollapsibleSection title="Załączniki i FAQ" subtitle="Opcjonalne">
                                        <VStack align="stretch" gap={4}>
                                            <ProductAttachments attachments={p.attachments ?? []} onChange={(v) => updateProduct(i, { attachments: v })} uploadInputId={`product-attachment-input-${p.id ?? i}`} />
                                            <FaqEditor faq={p.faq ?? []} onChange={(v) => updateProduct(i, { faq: v })} />
                                        </VStack>
                                    </CollapsibleSection>
                                </VStack>
                            </ItemCard>
                        );
                    })}
                    {products.length === 0 && (
                        <Text fontSize="xs" color="text.grey">
                            Dodaj przynajmniej jeden produkt lub usługę, żeby Agent wiedział, co sprzedaje.
                        </Text>
                    )}
                    <AddButton label="Dodaj produkt/usługę" onClick={addProduct} />
                </VStack>
            </CollapsibleSection>
        </VStack>
    );
};

export default Step3Products;
